import { ColumnDef } from '@tanstack/react-table'
import {
  Campaignservicev1Campaign,
  Campaignservicev1OrderLine,
  V1Results,
} from 'next-gen-sdk'
import { ProgressBar } from '../ProgressBar'
import { StatusChip, StatusChipColor } from '../StatusChip'
import { LinkButton } from '../../LinkButton'
import { simplifyEnum } from '../../../Helpers'

type RowExpansion = {
  orgName?: string
  orgId?: string
  stats?: V1Results
}

type OrderLineCalculatedStats = {
  ctr: number
  budgetServed: number
  budgetTotal: number
}

type CampaignCalculatedStats = {
  impressions: number
  clicks: number
  ctr: number
  budgetServed: number
  budgetTotal: number
}

type CampaignTableDataItem = Partial<Campaignservicev1Campaign> &
  RowExpansion & {
    calculatedStats?: CampaignCalculatedStats
  }

type OrderLineTableDataItem = Partial<Campaignservicev1OrderLine> &
  RowExpansion & {
    calculatedStats?: OrderLineCalculatedStats
  }

// Custom filter function for handling multiple values with OR logic (for status filters)
export const arrayIncludesFilterFn = (
  row: any,
  columnId: string,
  filterValue: any,
) => {
  // If filterValue is an array, check if the row value is included in the array (OR logic)
  if (Array.isArray(filterValue)) {
    const rowValue = row.getValue(columnId)
    return filterValue.includes(rowValue)
  }
  // If filterValue is a single value, use default comparison
  const rowValue = row.getValue(columnId)
  return rowValue === filterValue
}

// Custom filter function for string searches (case-insensitive partial match)
export const stringIncludesFilterFn = (
  row: any,
  columnId: string,
  filterValue: any,
) => {
  // If no filter value, show all rows
  if (!filterValue) return true

  const rowValue = row.getValue(columnId)
  if (!rowValue) return false

  const searchValue = rowValue.toString().toLowerCase()

  // Handle both single values and arrays
  if (Array.isArray(filterValue)) {
    // Check if any of the filter values match (OR logic for multiple string filters)
    return filterValue.some(
      (filter: string) => filter && searchValue.includes(filter.toLowerCase()),
    )
  } else {
    // Handle single string value
    const filter = filterValue.toString().toLowerCase()
    return searchValue.includes(filter)
  }
}

// Map status label to StatusChipColor
const statusToChipColor = (label?: string): StatusChipColor => {
  const key = (label || '').toLowerCase()
  switch (key) {
    //Orderline-specific options
    case 'active':
      return 'green'
    case 'approved':
      return 'orange'
    case 'completed':
      return 'blue'
    case 'paused':
      return 'gray'
    case 'rejected':
      return 'red'
    case 'archived':
      return 'yellow'
    // Campaign-specific options
    case 'open':
      return 'green'
    case 'serving':
      return 'blue'
    case 'deleted':
      return 'red'
    default:
      return 'gray'
  }
}

// Status Options and Extra Organization Column
const orderLineStatusValues = [
  'ORDERLINE_STATE_ACTIVE',
  'ORDERLINE_STATE_PAUSED',
  'ORDERLINE_STATE_COMPLETED',
  'ORDERLINE_STATE_ARCHIVED',
  'ORDERLINE_STATE_REJECTED',
  'ORDERLINE_STATE_APPROVED',
] as const
const campaignStatusValues = [
  'CAMPAIGN_STATUS_OPEN',
  'CAMPAIGN_STATUS_ARCHIVED',
] as const

type filterOptionsType = {
  label: string
  value: string
}

const orderLineStatusOptions: filterOptionsType[] = orderLineStatusValues.map(
  (value) => ({
    label: simplifyEnum(2, value),
    value,
  }),
)
const campaignStatusOptions: filterOptionsType[] = campaignStatusValues.map(
  (value) => ({
    label: simplifyEnum(2, value),
    value,
  }),
)

const createOrganizationColumn = (
  entityType: 'orderlines' | 'campaigns',
): ColumnDef<
  (CampaignTableDataItem | OrderLineTableDataItem) & RowExpansion
> => ({
  id: 'orgName',
  accessorKey: 'orgName',
  header: 'Organization',
  enableSorting: true,
  enableColumnFilter: true,
  filterFn: stringIncludesFilterFn,
  size: 180,
  minSize: 150,
  maxSize: 250,
  cell: ({ row }) => {
    const orgName = row.original?.orgName ?? ''
    const orgId = row.original?.orgId

    if (!orgName) return <span>{''}</span>

    if (orgId) {
      return (
        <LinkButton
          text={orgName}
          onClick={() => window.open(`/orgs/${orgId}/org-settings`, '_blank')}
          className="break-all"
        />
      )
    }

    return <span>{orgName}</span>
  },
  meta: {
    filterOn: 'string',
    removeFilter: false,
    removeSort: false,
  },
})

// Base column sets
const baseOrderLineColumns: ColumnDef<OrderLineTableDataItem>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Order Line Name',
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: stringIncludesFilterFn,
    size: 280,
    minSize: 200,
    maxSize: 400,
    cell: ({ row }) => {
      const orderLineName = row.original?.name ?? ''
      const orderLineId = row.original?.id
      const orgId = row.original?.orgId

      if (!orderLineName) return <span className="min-w-[15ch]">{''}</span>

      if (orderLineId && orgId) {
        return (
          <LinkButton
            text={orderLineName}
            onClick={() => {
              window.open(
                `/campaigns/dashboard/order-lines/${orderLineId}?org_id=${orgId}`,
                '_blank',
              )
            }}
            className="break-all text-[1rem] font-[500]"
          />
        )
      }

      return (
        <span className="min-w-[15ch] text-[1rem] font-[500]">
          {orderLineName}
        </span>
      )
    },
    meta: {
      filterOn: 'string',
      removeFilter: false,
      removeSort: false,
    },
  },
  {
    id: 'campaign.name',
    accessorFn: (row) =>
      (row as Partial<Campaignservicev1OrderLine>)?.campaign?.name,
    header: 'Campaign Name',
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: stringIncludesFilterFn,
    size: 200,
    minSize: 150,
    maxSize: 300,
    cell: ({ row }) => {
      const campaignName =
        (row.original as Partial<Campaignservicev1OrderLine>)?.campaign?.name ??
        ''
      const campaignId = (row.original as Partial<Campaignservicev1OrderLine>)
        ?.campaign?.id
      const orgId = row.original?.orgId

      if (!campaignName) return <span>{''}</span>

      if (campaignId && orgId) {
        return (
          <LinkButton
            text={campaignName}
            onClick={() => {
              window.open(
                `/campaigns/dashboard/${campaignId}?org_id=${orgId}`,
                '_blank',
              )
            }}
            className="break-all text-[1rem] font-[500]"
          />
        )
      }
      return <span className="text-[1rem] font-[500]">{campaignName}</span>
    },
    meta: {
      filterOn: 'string',
      removeFilter: false,
      removeSort: false,
    },
  },
  {
    id: 'state',
    accessorKey: 'state',
    header: 'Status',
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: arrayIncludesFilterFn,
    size: 100,
    minSize: 100,
    maxSize: 100,
    cell: ({ row }) => {
      const label = (row.original as Partial<Campaignservicev1OrderLine>)?.state
        ? simplifyEnum(
            2,
            (row.original as Partial<Campaignservicev1OrderLine>)?.state || '',
            true,
          )
        : ''
      return (
        <StatusChip
          color={statusToChipColor(label)}
          label={label}
          className="font-[600]"
        />
      )
    },
    meta: {
      filterOn: orderLineStatusOptions,
      removeFilter: false,
      removeSort: false,
    },
  },
  {
    id: 'totalImpressions',
    accessorKey: 'totalImpressions',
    header: 'Total Impressions',
    enableSorting: false,
    enableColumnFilter: false,
    size: 95,
    minSize: 95,
    maxSize: 160,
    cell: ({ row }) => {
      const total = Number(
        (row.original as Partial<Campaignservicev1OrderLine>)?.impressions ?? 0,
      )
      return (
        <div className="w-full tabular-nums">
          {Number.isFinite(total) ? total.toLocaleString('en-US') : '-'}
        </div>
      )
    },
    meta: {
      removeFilter: true,
      removeSort: true,
    },
  },
  {
    id: 'servedImpressions',
    accessorKey: 'servedImpressions',
    header: 'Served Impressions',
    enableSorting: false,
    enableColumnFilter: false,
    size: 100,
    minSize: 100,
    maxSize: 170,
    cell: ({ row }) => {
      const served = Number(row.original?.stats?.totals?.imps ?? 0)
      return (
        <div className="w-full tabular-nums">
          {Number.isFinite(served) ? served.toLocaleString('en-US') : '-'}
        </div>
      )
    },
    meta: {
      removeFilter: true,
      removeSort: true,
    },
  },
  {
    id: 'clicks',
    accessorKey: 'clicks',
    header: 'Clicks',
    enableSorting: false,
    enableColumnFilter: false,
    size: 60,
    minSize: 60,
    maxSize: 120,
    cell: ({ row }) => {
      const value = Number(row.original?.stats?.totals?.clicks ?? 0)
      return (
        <div className="w-full tabular-nums">
          {Number.isFinite(value) ? value.toLocaleString('en-US') : '-'}
        </div>
      )
    },
    meta: {
      removeFilter: true,
      removeSort: true,
    },
  },
  {
    id: 'ctr',
    accessorKey: 'ctr',
    header: 'CTR',
    enableSorting: false,
    enableColumnFilter: false,
    size: 60,
    minSize: 60,
    maxSize: 100,
    cell: ({ row }) => {
      const ctr = Number(row.original?.calculatedStats?.ctr ?? 0)
      const pct = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(ctr)
      return <div className="w-full tabular-nums">{pct}%</div>
    },
    meta: {
      removeFilter: true,
      removeSort: true,
    },
  },
  {
    id: 'budget',
    accessorKey: 'budget',
    header: 'Budget',
    enableSorting: false,
    enableColumnFilter: false,
    size: 200,
    minSize: 200,
    maxSize: 250,
    cell: ({ row }) => {
      const current = Number(row.original?.calculatedStats?.budgetServed ?? 0)
      const goal = Number(row.original?.calculatedStats?.budgetTotal ?? 0)
      const fmt = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      const currentDisplay = current > goal ? goal : current
      const ratioText = `${
        Number.isFinite(currentDisplay) ? fmt.format(currentDisplay) : '-'
      } / ${Number.isFinite(goal) ? fmt.format(goal) : '-'}`
      return (
        <div className="flex w-full items-center gap-2">
          <span className="whitespace-nowrap !font-[600] tabular-nums">
            {ratioText}
          </span>
          <div className="flex-1">
            <ProgressBar current={currentDisplay} goal={goal} />
          </div>
        </div>
      )
    },
    meta: {
      removeFilter: true,
      removeSort: true,
    },
  },
]

const baseCampaignColumns: ColumnDef<CampaignTableDataItem>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Campaign Name',
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: stringIncludesFilterFn,
    size: 280,
    minSize: 200,
    maxSize: 400,
    cell: ({ row }) => {
      const campaignName = row.original?.name ?? ''
      const campaignId = row.original?.id
      const orgId = row.original?.orgId

      if (!campaignName) return <span>{''}</span>

      if (campaignId && orgId) {
        return (
          <LinkButton
            text={campaignName}
            onClick={() => {
              window.open(
                `/campaigns/dashboard/${campaignId}?org_id=${orgId}`,
                '_blank',
              )
            }}
            className="break-all text-[1rem] font-[500]"
          />
        )
      }

      return <span className="text-[1rem] font-[500]">{campaignName}</span>
    },
    meta: {
      filterOn: 'string',
      removeFilter: false,
      removeSort: false,
    },
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: arrayIncludesFilterFn,
    size: 100,
    minSize: 100,
    maxSize: 150,
    cell: ({ row }) => {
      const label = simplifyEnum(2, row.original.status || '', true)
      return (
        <StatusChip
          color={statusToChipColor(label)}
          label={label}
          className="font-[600]"
        />
      )
    },
    meta: {
      filterOn: campaignStatusOptions,
      removeFilter: false,
      removeSort: false,
    },
  },
  {
    id: 'totalImpressions',
    accessorKey: 'totalImpressions',
    header: 'Total Impressions',
    enableSorting: false,
    enableColumnFilter: false,
    size: 87,
    minSize: 87,
    maxSize: 170,
    cell: ({ row }) => {
      const total = Number(row.original?.calculatedStats?.impressions ?? 0)
      return (
        <div className="w-full tabular-nums">
          {Number.isFinite(total) ? total.toLocaleString('en-US') : '-'}
        </div>
      )
    },
    meta: {
      removeFilter: true,
      removeSort: true,
    },
  },
  {
    id: 'servedImpressions',
    accessorKey: 'servedImpressions',
    header: 'Served Impressions',
    enableSorting: false,
    enableColumnFilter: false,
    size: 90,
    minSize: 90,
    maxSize: 170,
    cell: ({ row }) => {
      const served = Number(row.original?.stats?.totals?.imps ?? 0)
      return (
        <div className="w-full tabular-nums">
          {Number.isFinite(served) ? served.toLocaleString('en-US') : '-'}
        </div>
      )
    },
    meta: {
      removeFilter: true,
      removeSort: true,
    },
  },
  {
    id: 'clicks',
    accessorKey: 'clicks',
    header: 'Clicks',
    enableSorting: false,
    enableColumnFilter: false,
    size: 60,
    minSize: 60,
    maxSize: 130,
    cell: ({ row }) => {
      const value = Number(row.original?.calculatedStats?.clicks ?? 0)
      return (
        <div className="w-full tabular-nums">
          {Number.isFinite(value) ? value.toLocaleString('en-US') : '-'}
        </div>
      )
    },
    meta: {
      removeFilter: true,
      removeSort: true,
    },
  },
  {
    id: 'ctr',
    accessorKey: 'ctr',
    header: 'CTR',
    enableSorting: false,
    enableColumnFilter: false,
    size: 60,
    minSize: 60,
    maxSize: 130,
    cell: ({ row }) => {
      const value = Number(row.original?.calculatedStats?.ctr ?? 0)
      const pct = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
      return <div className="w-full tabular-nums">{pct}%</div>
    },
    meta: {
      removeFilter: true,
      removeSort: true,
    },
  },
  {
    id: 'budget',
    accessorKey: 'budget',
    header: 'Budget',
    enableSorting: false,
    enableColumnFilter: false,
    size: 200,
    minSize: 180,
    maxSize: 250,
    cell: ({ row }) => {
      const current = Number(row.original?.calculatedStats?.budgetServed ?? 0)
      const goal = Number(row.original?.calculatedStats?.budgetTotal ?? 0)
      const fmt = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      const currentDisplay = current > goal ? goal : current
      const ratioText = `${
        Number.isFinite(currentDisplay) ? fmt.format(currentDisplay) : '-'
      } / ${Number.isFinite(goal) ? fmt.format(goal) : '-'}`
      return (
        <div className="flex w-full items-center gap-2">
          <span className="whitespace-nowrap font-[500] tabular-nums">
            {ratioText}
          </span>
          <div className="flex-1">
            <ProgressBar current={currentDisplay} goal={goal} />
          </div>
        </div>
      )
    },
    meta: {
      removeFilter: true,
      removeSort: true,
    },
  },
]

// Helpers to derive final column sets without mutating base arrays
const insertAt = <T,>(arr: readonly T[], index: number, item: T): T[] => [
  ...arr.slice(0, index),
  item,
  ...arr.slice(index),
]

export const getOrderLineColumns = (
  includeChildOrgs: boolean,
): ColumnDef<OrderLineTableDataItem>[] => {
  return includeChildOrgs
    ? insertAt(
        baseOrderLineColumns,
        2,
        createOrganizationColumn(
          'orderlines',
        ) as ColumnDef<OrderLineTableDataItem>,
      )
    : [...baseOrderLineColumns]
}

export const getCampaignColumns = (
  includeChildOrgs: boolean,
): ColumnDef<CampaignTableDataItem>[] => {
  return includeChildOrgs
    ? insertAt(
        baseCampaignColumns,
        1,
        createOrganizationColumn(
          'campaigns',
        ) as ColumnDef<CampaignTableDataItem>,
      )
    : [...baseCampaignColumns]
}
